// ─── Non-virtualized DataGrid table renderer ────────────────────────────────
//
// Thin `mantine-datatable` wrapper — real `<table>` markup plus mantine-
// datatable's native column resizing and first/last column pinning, layered
// on top of the same `ColumnMeta`-driven filter/sort/aggregate engine
// (`gridEngine.ts`). The custom `DataGridColumnHeader` (multi-column sort +
// enum/range/date filter popovers) is passed in as-is via `renderHeader` —
// mantine-datatable's own `sortStatus`/`onSortStatusChange` only supports a
// single sort column at a time, so we intentionally keep the existing
// multi-column sort UI rather than switch to mantine-datatable's native sort
// affordance.
//
// Used by `PropertiesTable` (small-to-moderate, fully-loaded row sets, ~100s
// to a couple thousand rows) where mantine-datatable's native feature set

import React, { type ReactNode, useEffect, useMemo, useState } from 'react';
import { DataTable, type DataTableColumn, useDataTableColumns } from 'mantine-datatable';
import { Box, Group, Text } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { ColumnConfig, ColumnMetaMap } from './types';
import { type GroupedRowBucket, groupGridRows, sortGridItems, sortGroupedBuckets } from './gridEngine';
import { DataGridColumnHeader } from './DataGridColumnHeader';

/**
 * Resolves a `ColumnMeta.width` into a value usable by a real `<table>`
 * column (mantine-datatable).
 *
 * - No width defined → returns `undefined`, so the column fits its
 *   title/content text naturally (paired with `noWrap` on the header so the
 *   title doesn't wrap and shrink the column). This is the sensible default
 *   for a column that didn't opt into an explicit width.
 * - Fractional/grid units (`'2fr'`, `'1fr'`) are only meaningful in CSS grid;
 *   in a `<table>` they're an invalid `width` and the column collapses to a
 *   ~8px sliver. Map them to pixels scaled by the `fr` factor.
 * - Fixed px / `%` widths pass through unchanged.
 */
function resolveTableColumnWidth(width: number | string | undefined): number | string | undefined {
  if (width == null) return undefined;
  if (typeof width === 'number') return width;
  const fr = width.trim().match(/^([\d.]+)fr$/i);
  if (fr) return Math.round(parseFloat(fr[1]) * DEFAULT_FR_WIDTH_PX);
  return width;
}

/** Base pixel width one `1fr` maps to in the real-`<table>` renderer. */
const DEFAULT_FR_WIDTH_PX = 160;

/**
 * Props for the DataGridTable component that renders a non-virtualized mantine-datatable-backed table with grouping support.
 */
export interface DataGridTableProps<TRow, K extends string> {
  rows: TRow[];
  idAccessor: keyof TRow | ((row: TRow) => string | number);
  columns: ColumnConfig<K>[];
  columnMeta: ColumnMetaMap<TRow, K>;
  renderCell: (columnKey: K, row: TRow) => ReactNode;
  renderHeader: (columnKey: K) => ReactNode;
  onRowClick?: (row: TRow) => void;
  noRecordsText?: string;
  height?: string | number;
  /** Precomputed footer aggregate text per column (see `computeColumnAggregates` in `gridEngine.ts`). */
  aggregates?: Partial<Record<K, string>>;
  /**
   * Optional override for rendering a column's footer aggregate cell — receives the
   * precomputed aggregate text from `aggregates`. Falls back to plain text if omitted.
   * Useful for e.g. applying the same visual gating/blur treatment used in `renderCell`
   * to a gated column's footer total (so a hidden/blurred value doesn't leak through the
   * aggregate row). Still fully dynamic — recomputed by the caller alongside `aggregates`
   * on every filter/sort change, only the *presentation* of the value is overridden here.
   */
  renderFooter?: (columnKey: K, aggregateText: string) => ReactNode;
  /** Enables per-column drag-resize (mantine-datatable native). Defaults to true. */
  resizable?: boolean;
  /**
   * localStorage key under which mantine-datatable persists user-resized
   * column widths (via its `useDataTableColumns`). Required for resize widths
   * to survive reloads AND for `onResetWidthsRef` to work. Must be unique per
   * table. When omitted, resizing still works in-session but isn't persisted
   * and there's no reset handle.
   */
  storeColumnsKey?: string;
  /**
   * Populated by this component with a function that resets all user-resized
   * column widths back to their `ColumnMeta.width` defaults (mantine-datatable's
   * `resetColumnsWidth`). A sibling control (e.g. the preset bar's "Reset
   * column widths" button) can call `ref.current?.()`. Only meaningful with
   * `storeColumnsKey` set.
   */
  onResetWidthsRef?: React.MutableRefObject<(() => void) | null>;
  /** Pins the first visible column to the left edge while horizontally scrolling (mantine-datatable native — first/last only, not arbitrary columns). */
  pinFirstColumn?: boolean;
  /** Pins the last visible column to the right edge while horizontally scrolling (mantine-datatable native — first/last only, not arbitrary columns). */
  pinLastColumn?: boolean;
  /** CSS table layout mode. Fixed layout matches the legacy transaction table's sizing behavior. */
  tableLayout?: 'auto' | 'fixed';
  /**
   * When set to a groupable column key, renders collapsible summary rows
   * instead of a flat row list (via `groupGridRows`): one bucket per distinct
   * value of that column. Each group row shows a chevron + label + its row
   * count; expanding it reveals that group's individual member rows (via
   * `GroupMemberTable`) with the same `renderCell`/`renderHeader`. The active
   * group-by column always renders first. `undefined`/`null` (the default)
   * renders the flat row list as before.
   */
  groupByColumnKey?: K | null;
  /**
   * Active multi-column sort — only consulted in grouped mode, to order the
   * group buckets themselves (via `sortGroupedBuckets`). Ignored in flat
   * mode, since the caller is expected to have already sorted `rows` there.
   */
  sortColumns?: string[];
  sortOrders?: ('asc' | 'desc' | 'none')[];
  /**
   * Max height of each expanded group's member-row list before it becomes
   * independently scrollable — keeps a large group from pushing the rest of
   * the groups off-screen. Defaults to 320px.
   */
  groupContentMaxHeight?: number | string;
  /**
   * Width of the group-by column when grouping is active — overrides that
   * column's normal `ColumnMeta.width` for the duration of grouping, since it
   * now has to fit a chevron, label, and row count rather than just its
   * usual single-line value. Defaults to 280px. Also used to size the
   * matching leading spacer column in each expanded group's member table, so
   * the two stay pixel-aligned.
   */
  groupColumnWidth?: number | string;
}

// ─── Sortable member table (rendered inside an expanded group) ─────────────
//
// Sorting here is local to this one group's rows and completely independent
// of the outer table's sort (which orders the group buckets themselves via
// `sortGroupedBuckets`) — each expanded group gets its own single-column
// sort state, reset whenever the group is collapsed and re-expanded (this
// component unmounts with the group's `rowExpansion` content).

/**
 * Props for the GroupMemberTable component that renders member rows of one expanded group as its own small DataTable.
 */
interface GroupMemberTableProps<TRow, K extends string> {
  rows: TRow[];
  idAccessor: keyof TRow | ((row: TRow) => string | number);
  columns: ColumnConfig<K>[];
  columnMeta: ColumnMetaMap<TRow, K>;
  renderCell: (columnKey: K, row: TRow) => ReactNode;
  onRowClick?: (row: TRow) => void;
  maxHeight?: number | string;
  resizable: boolean;
}

/**
 * Renders the member rows of one expanded group as its own small DataTable, with an independent single-column sort scoped to just that group.
 *
 * @param rows - The member rows belonging to this one expanded group.
 * @param idAccessor - Row id accessor, same as the parent table's.
 * @param columns - Visible columns to render (excludes the group-by column).
 * @param columnMeta - Column metadata map.
 * @param renderCell - Cell renderer, same as the parent table's.
 * @param onRowClick - Row click handler, same as the parent table's.
 * @param maxHeight - Max height before the member list becomes independently scrollable.
 * @param resizable - Whether column drag-resize is enabled.
 */
function GroupMemberTable<TRow, K extends string>({
  rows,
  idAccessor,
  columns,
  columnMeta,
  renderCell,
  onRowClick,
  maxHeight,
  resizable,
}: GroupMemberTableProps<TRow, K>) {
  const [sortColumn, setSortColumn] = useState<K | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | 'none'>('none');

  const sortedRows = useMemo(() => {
    if (!sortColumn || sortOrder === 'none') return rows;
    return sortGridItems(rows, [String(sortColumn)], [sortOrder], columnMeta);
  }, [rows, sortColumn, sortOrder, columnMeta]);

  const handleSort = (columnKey: string, order: 'asc' | 'desc' | 'none') => {
    setSortColumn(order === 'none' ? null : (columnKey as K));
    setSortOrder(order);
  };

  const memberColumns: DataTableColumn<TRow>[] = columns.map((col, index) => {
    const meta = columnMeta[col.key];
    return {
      accessor: String(col.key),
      title: (
        <DataGridColumnHeader
          columnKey={String(col.key)}
          label={meta?.label ?? String(col.key)}
          sortable={meta?.sortable}
          sortColumns={sortColumn != null ? [String(sortColumn)] : []}
          sortOrders={sortColumn != null ? [sortOrder] : []}
          onSort={handleSort}
          onFilterChange={() => undefined}
        />
      ) as never,
      // Only the first column gets a small indent, so the sub-table still
      // reads as nested under its group row — every other column keeps
      // the exact same width/position as its counterpart in the group row
      // above (see `DataGridTable`'s `memberTableColumns`).
      render: (row: TRow) =>
        index === 0 ? <Box pl={16}>{renderCell(col.key, row)}</Box> : renderCell(col.key, row),
      width: resolveTableColumnWidth(meta?.width),
      resizable,
    };
  });

  return (
    <DataTable
      withTableBorder={false}
      withRowBorders={false}
      // A definite pixel height (not just `maxHeight`) is required here —
      // this table lives inside the group row's `rowExpansion` Collapse
      // cell, which has no definite height of its own (it auto-sizes to
      // content). mantine-datatable's own internal ScrollArea + sticky
      // header mechanism needs a real, absolute height to size against;
      // wrapping this in our own separately-scrolling container (an earlier
      // approach) created two nested scroll contexts and broke the sticky
      // header, since it positioned itself relative to mantine-datatable's
      // own (unconstrained) internal ScrollArea rather than our wrapper.
      // Only constrained once there are enough rows to actually need it, so
      // small groups don't get padded out with empty space.
      height={sortedRows.length > 6 ? maxHeight : undefined}
      records={sortedRows}
      idAccessor={idAccessor as never}
      columns={memberColumns}
      highlightOnHover
      fz='sm'
      onRowClick={onRowClick ? ({ record }) => onRowClick(record) : undefined}
    />
  );
}

/**
 * Renders a non-virtualized `mantine-datatable`-backed table for a DataGrid, driven by `ColumnMeta`/preset state — supports column resizing/pinning, footer aggregates, and an optional grouped mode that shows one collapsible summary row per distinct value of a chosen column, each expandable into its own member-row sub-table.
 */
export function DataGridTable<TRow, K extends string>({
  rows,
  idAccessor,
  columns,
  columnMeta,
  renderCell,
  renderHeader,
  onRowClick,
  noRecordsText = 'No records found',
  height = '100%',
  aggregates,
  renderFooter,
  resizable = true,
  pinFirstColumn = false,
  pinLastColumn = false,
  tableLayout = 'auto',
  groupByColumnKey,
  sortColumns = [],
  sortOrders = [],
  groupContentMaxHeight = 320,
  groupColumnWidth = 280,
  storeColumnsKey,
  onResetWidthsRef,
}: DataGridTableProps<TRow, K>) {
  const visible = [...columns].filter((c) => c.visible).sort((a, b) => a.order - b.order);
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<string[]>([]);

  const dataTableColumns: DataTableColumn<TRow>[] = visible.map((col) => {
    const aggregateText = columnMeta[col.key]?.aggregate ? aggregates?.[col.key] : undefined;
    const width = resolveTableColumnWidth(columnMeta[col.key]?.width);
    return {
      accessor: String(col.key),
      title: renderHeader(col.key) as never,
      render: (row: TRow) => renderCell(col.key, row),
      footer: aggregateText != null ? (renderFooter ? renderFooter(col.key, aggregateText) : aggregateText) : undefined,
      width,
      // No explicit width => let the column fit its title/content; `noWrap`
      // stops the title from wrapping (which would otherwise shrink it).
      noWrap: width == null ? true : undefined,
      resizable,
    };
  });

  // Persisted resize widths + a reset handle (mantine-datatable native).
  // `storeColumnsKey` keys the localStorage entry; without it, resizing still
  // works in-session but nothing persists and reset is a no-op. `effectiveColumns`
  // merges the user's saved widths over `dataTableColumns` — it's what the flat
  // (non-grouped) table renders. Called unconditionally (before the grouped
  // early-return below) to satisfy the rules of hooks.
  const { effectiveColumns, resetColumnsWidth } = useDataTableColumns<TRow>({
    key: storeColumnsKey,
    columns: dataTableColumns,
  });

  useEffect(() => {
    if (!onResetWidthsRef) return;
    const ref = onResetWidthsRef;
    ref.current = resetColumnsWidth;
    return () => {
      if (ref.current === resetColumnsWidth) ref.current = null;
    };
  }, [onResetWidthsRef, resetColumnsWidth]);

  if (groupByColumnKey != null) {
    const rawBuckets = groupGridRows(rows, groupByColumnKey, columnMeta);
    const buckets = sortGroupedBuckets(rawBuckets, groupByColumnKey, sortColumns, sortOrders, columnMeta);

    // The group-by column renders first; the rest keep their normal order.
    const orderedForGrouping = [
      ...visible.filter((c) => c.key === groupByColumnKey),
      ...visible.filter((c) => c.key !== groupByColumnKey),
    ];

    // The member table excludes the group-by column — its value is shared
    // across the whole group (that's what makes it a group), so repeating it
    // per member row is pure noise.
    const memberTableColumns = visible.filter((c) => c.key !== groupByColumnKey);

    const groupColumns: DataTableColumn<GroupedRowBucket<TRow, K>>[] = orderedForGrouping.map((col) => {
      if (col.key === groupByColumnKey) {
        return {
          accessor: 'key',
          title: renderHeader(col.key) as never,
          render: (bucket) => (
            <Group gap={6} wrap='nowrap'>
              <IconChevronRight
                size={14}
                style={{
                  transition: 'transform 150ms',
                  transform: expandedGroupKeys.includes(bucket.key) ? 'rotate(90deg)' : 'rotate(0deg)',
                }}
              />
              <Text fz='sm' fw={600}>
                {bucket.label}
              </Text>
              <Text fz='xs' c='dimmed'>
                ({bucket.rows.length})
              </Text>
            </Group>
          ),
          width: groupColumnWidth,
          resizable,
        };
      }

      return {
        accessor: String(col.key),
        title: renderHeader(col.key) as never,
        render: (bucket) => {
          const text = bucket.aggregates[col.key];
          if (text == null) return null;
          return renderFooter ? renderFooter(col.key, text) : <Text fz='sm' fw={500}>{text}</Text>;
        },
        width: resolveTableColumnWidth(columnMeta[col.key]?.width),
      resizable,
    };
  });

    return (
      <DataTable
        withTableBorder
        borderRadius='md'
        height={height}
        records={buckets}
        idAccessor='key'
        columns={groupColumns}
        highlightOnHover
        striped
        fz='sm'
        noRecordsText={noRecordsText}
        rowExpansion={{
          allowMultiple: true,
          expanded: { recordIds: expandedGroupKeys, onRecordIdsChange: setExpandedGroupKeys },
          content: ({ record }) => (
            <GroupMemberTable
              rows={record.rows}
              idAccessor={idAccessor}
              columns={memberTableColumns}
              columnMeta={columnMeta}
              renderCell={renderCell}
              onRowClick={onRowClick}
              maxHeight={groupContentMaxHeight}
              resizable={resizable}
            />
          ),
        }}
      />
    );
  }

  return (
    <DataTable
      withTableBorder
      borderRadius='md'
      height={height}
      records={rows}
      idAccessor={idAccessor as never}
        columns={effectiveColumns}
        styles={{ table: { tableLayout } }}
      storeColumnsKey={storeColumnsKey}
      pinFirstColumn={pinFirstColumn}
      pinLastColumn={pinLastColumn}
      highlightOnHover
      striped
      fz='sm'
      onRowClick={onRowClick ? ({ record }) => onRowClick(record) : undefined}
      noRecordsText={noRecordsText}
    />
  );
}

export default DataGridTable;
